import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, FileText, Download, PhoneCall, CheckCircle2, CreditCard } from "lucide-react";
import { PortalLayout } from "../../components/PortalLayout";
import { BookCallDialog } from "../../components/BookCallDialog";
import { api, formatApiError } from "../../lib/api";

const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";

const EMPTY = { title: "", employment_type: "", location: "", salary_range: "", summary: "",
  required_qualifications: "", required_experience: "", preferred_attributes: "", start_date: "" };

export default function SubmitRole() {
  const [form, setForm] = useState(EMPTY);
  const [pdFile, setPdFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gate, setGate] = useState(null);
  const [signName, setSignName] = useState("");
  const [signAgreed, setSignAgreed] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadGate = useCallback(() => {
    api.get("/employer/dashboard").then((res) => {
      const consents = res.data.consents || [];
      const usage = res.data.membership_usage;
      setGate({
        packSigned: consents.some((c) => c.document_name === "membership_pack"),
        subActive: (res.data.company?.subscription?.status) === "active",
        overLimit: usage ? usage.placements_used >= usage.placements_limit : false,
        usage,
      });
    }).catch(() => setGate({ packSigned: false, subActive: false, overLimit: false }));
  }, []);
  useEffect(() => { loadGate(); }, [loadGate]);

  const signPack = async (e) => {
    e.preventDefault();
    if (!signAgreed) return toast.error("Please confirm you've read and agree to the Membership Pack");
    setSignBusy(true);
    try {
      await api.post("/consent/sign", { document_name: "membership_pack", typed_signature: signName, agreed: true });
      toast.success("Membership Pack signed — thank you!");
      loadGate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSignBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: role } = await api.post("/employer/roles", form);
      if (pdFile) {
        const fd = new FormData();
        fd.append("doc_type", "position_description");
        fd.append("file", pdFile);
        const { data: doc } = await api.post("/documents/upload", fd);
        await api.put(`/employer/roles/${role.id}/pd?doc_id=${doc.id}`);
      }
      toast.success("Role submitted — our recruiters are on it");
      navigate("/employer");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!gate)
    return <PortalLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0d2f4d]" /></div></PortalLayout>;

  if (!gate.packSigned)
    return (
      <PortalLayout>
        <div className="max-w-2xl" data-testid="membership-pack-gate">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Before you list a role</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Sign the Employer Membership Pack</h1>
          <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
            Everything about working with us — memberships, fees, the 3-month replacement guarantee, cancellation and
            our terms — lives in one document. Have a read, then sign digitally below to unlock role listings.
          </p>
          <a href="/membership-pack.pdf" download="The_Ditch_Careers_Employer_Membership_Pack.pdf" data-testid="download-membership-pack"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3 hover:bg-[#0d2f4d] hover:text-white transition-colors">
            <Download size={15} /> Download the Membership Pack (PDF)
          </a>
          <form onSubmit={signPack} className="ditch-card p-6 sm:p-8 mt-8 space-y-5">
            <div><label className={labelCls}>Your full legal name (digital signature)</label>
              <input required value={signName} onChange={(e) => setSignName(e.target.value)}
                placeholder="e.g. Jane Elizabeth Smith" data-testid="pack-signature-input" className={inputCls} /></div>
            <label className="flex items-start gap-3 text-sm text-[#51637a] cursor-pointer">
              <input type="checkbox" checked={signAgreed} onChange={(e) => setSignAgreed(e.target.checked)}
                data-testid="pack-agree-checkbox" className="mt-0.5 h-4 w-4 accent-[#C1CC24]" />
              I confirm I have read the Employer Membership Pack, I agree to the Membership Terms, fees, guarantee and
              cancellation policy, and I am authorised to enter this agreement on behalf of my business.
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="submit" disabled={signBusy} data-testid="pack-sign-button"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-3.5 hover:bg-[#aeb81f] transition-colors disabled:opacity-60">
                {signBusy ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {signBusy ? "Signing..." : "Sign & continue"}
              </button>
              <button type="button" onClick={() => setCallOpen(true)} data-testid="pack-book-call"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3.5 hover:bg-[#0d2f4d] hover:text-white transition-colors">
                <PhoneCall size={15} /> More questions? Book a call
              </button>
            </div>
          </form>
        </div>
        <BookCallDialog open={callOpen} onOpenChange={setCallOpen} />
      </PortalLayout>
    );

  if (!gate.subActive)
    return (
      <PortalLayout>
        <div className="max-w-2xl" data-testid="membership-payment-gate">
          <p className="flex items-center gap-2 text-sm font-medium text-[#7C8514]"><CheckCircle2 size={16} /> Membership Pack signed</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-3">One more step — set up your membership</h1>
          <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
            Role listings unlock once your membership payment is active. Choose your tier and pay securely with Stripe,
            or book a call and we'll work out what's right for you.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/employer/partnership" data-testid="setup-payment-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-3.5 hover:bg-[#aeb81f] transition-colors">
              <CreditCard size={16} /> Set up my membership payment
            </Link>
            <button onClick={() => setCallOpen(true)} data-testid="payment-book-call"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3.5 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              <PhoneCall size={15} /> Book a call to discuss
            </button>
          </div>
          <p className="text-xs text-[#51637a] mt-5">
            Already paid manually or arranged something with our team? Sit tight — we'll unlock your account shortly.
          </p>
        </div>
        <BookCallDialog open={callOpen} onOpenChange={setCallOpen} />
      </PortalLayout>
    );

  if (gate.overLimit)
    return (
      <PortalLayout>
        <div className="max-w-2xl" data-testid="placement-limit-gate">
          <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">Membership limit reached</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">
            You've used all {gate.usage?.placements_limit} placements in your {gate.usage?.plan_name}
          </h1>
          <p className="text-sm text-[#51637a] mt-2 leading-relaxed">
            Great news — your membership has been working hard. To list more roles, upgrade your membership tier,
            or book a call and we'll arrange additional placements at your exclusive member rate.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/employer/partnership" data-testid="upgrade-membership-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-3.5 hover:bg-[#aeb81f] transition-colors">
              <CreditCard size={16} /> Upgrade my membership
            </Link>
            <button onClick={() => setCallOpen(true)} data-testid="limit-book-call"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#0d2f4d] text-[#0d2f4d] font-medium text-sm px-6 py-3.5 hover:bg-[#0d2f4d] hover:text-white transition-colors">
              <PhoneCall size={15} /> Book a call about member rates
            </button>
          </div>
        </div>
        <BookCallDialog open={callOpen} onOpenChange={setCallOpen} />
      </PortalLayout>
    );

  return (
    <PortalLayout>
      <div data-testid="submit-role-page">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">New role request</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Submit a role</h1>
        <p className="text-sm text-[#51637a] mt-1">Tell us what you need — the more detail, the better the shortlist.</p>

        <form onSubmit={submit} className="ditch-card p-6 sm:p-8 mt-8 max-w-2xl space-y-5">
          <div><label className={labelCls}>Job title *</label>
            <input required value={form.title} onChange={set("title")} data-testid="role-title" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Employment type *</label>
              <select required value={form.employment_type} onChange={set("employment_type")} data-testid="role-employment-type" className={inputCls}>
                <option value="">Select type</option>
                <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Casual / Seasonal</option>
              </select></div>
            <div><label className={labelCls}>Location *</label>
              <input required value={form.location} onChange={set("location")} data-testid="role-location" className={inputCls} placeholder="e.g. Queenstown, NZ" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Salary / rate range *</label>
              <input required value={form.salary_range} onChange={set("salary_range")} data-testid="role-salary" className={inputCls} placeholder="e.g. $75k–$90k + super" /></div>
            <div><label className={labelCls}>Start date / urgency</label>
              <input value={form.start_date} onChange={set("start_date")} data-testid="role-start-date" className={inputCls} placeholder="e.g. ASAP, or 1 Aug 2026" /></div>
          </div>
          <div><label className={labelCls}>Role summary *</label>
            <textarea required rows={4} value={form.summary} onChange={set("summary")} data-testid="role-summary" className={inputCls} /></div>
          <div><label className={labelCls}>Required qualifications</label>
            <textarea rows={2} value={form.required_qualifications} onChange={set("required_qualifications")} data-testid="role-qualifications" className={inputCls} /></div>
          <div><label className={labelCls}>Required experience</label>
            <textarea rows={2} value={form.required_experience} onChange={set("required_experience")} data-testid="role-experience" className={inputCls} /></div>
          <div><label className={labelCls}>Preferred attributes</label>
            <textarea rows={2} value={form.preferred_attributes} onChange={set("preferred_attributes")} data-testid="role-attributes" className={inputCls} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Software used in your business</label>
              <input value={form.software_used} onChange={set("software_used")} data-testid="role-software-used" className={inputCls} placeholder="e.g. Xero, Salesforce, POS systems" /></div>
            <div><label className={labelCls}>Team culture</label>
              <input value={form.team_culture} onChange={set("team_culture")} data-testid="role-team-culture" className={inputCls} placeholder="e.g. tight-knit, fast-paced, social" /></div>
          </div>
          <div>
            <label className={labelCls}>Position description (optional)</label>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="role-pd-upload-button"
              className="mt-1.5 flex w-full items-center gap-3 rounded-lg border border-dashed border-[#e3e0d3] px-4 py-3.5 text-sm text-[#51637a] hover:border-[#0d2f4d] transition-colors">
              <Upload size={16} /> {pdFile ? pdFile.name : "Attach a PD document"}
            </button>
            <input ref={fileRef} type="file" className="hidden" data-testid="role-pd-input"
              onChange={(e) => setPdFile(e.target.files?.[0] || null)} />
          </div>
          <button type="submit" disabled={busy} data-testid="submit-role-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] font-medium px-8 py-3.5 hover:bg-[#aeb81f] transition-colors disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? "Submitting..." : "Submit role request"}
          </button>
        </form>
      </div>
    </PortalLayout>
  );
}
